import { useEffect, useState } from 'react'
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import { useAuth } from './AuthContext'
import type { ReactNode } from 'react'
import type { LDContext } from 'launchdarkly-js-client-sdk'

type LDProviderComponent = React.ComponentType<{ children: ReactNode }>

const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_SIDE_ID as
  | string
  | undefined

export function LaunchDarklyProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  const [LDProvider, setLDProvider] = useState<LDProviderComponent | null>(null)

  useEffect(() => {
    if (isLoading) return
    if (!user) return
    if (!clientSideID) {
      if (import.meta.env.DEV) {
        console.warn(
          '[LaunchDarkly] VITE_LAUNCHDARKLY_CLIENT_SIDE_ID not set; flags disabled.',
        )
      }
      return
    }

    const context: LDContext = {
      kind: 'user',
      key: user.id,
      email: user.email,
    }

    let cancelled = false
    asyncWithLDProvider({
      clientSideID,
      context,
      reactOptions: { useCamelCaseFlagKeys: true },
      // Bound how long we wait for LD to initialise (seconds) — this is passed
      // to the JS SDK's waitForInitialization. Without it the SDK warns and, in
      // a future version, would apply its own default. Children render unwrapped
      // (flags default off) until this resolves, so a generous value is safe.
      timeout: 5,
    }).then((Provider) => {
      if (!cancelled) setLDProvider(() => Provider)
    })

    return () => {
      cancelled = true
    }
  }, [isLoading, user?.id, user?.email])

  if (!clientSideID) return <>{children}</>
  if (!LDProvider) return <>{children}</>
  return <LDProvider>{children}</LDProvider>
}
